import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Check, MapPin, DoorClosed } from "lucide-react";
import { getOnsite, confirmOnsite, PUBLIC_TOKEN } from "@/lib/api";
import { ContactFooter } from "@/components/ui-bits";

const SHIFTS = ["1st Shift", "2nd Shift", "3rd Shift", "Open to Any"];
const REFERRAL_SOURCES = ["Recruiting partner", "Halfway / recovery house", "CareerLink-type organization", "Referral (individual)", "Walk-in / on-site", "Other"];
const NEEDS_ORG = new Set(["Recruiting partner", "Halfway / recovery house", "CareerLink-type organization", "Other"]);

function Pick({ options, value, onChange, testidPrefix }) {
  return (
    <div className="ca-pickrow" data-testid={testidPrefix}>
      {options.map((o) => (
        <button key={String(o.v ?? o)} type="button" className={`ca-pick${value === (o.v ?? o) ? " on" : ""}`}
          onClick={() => onChange(o.v ?? o)} data-testid={`${testidPrefix}-${String(o.v ?? o)}`}>{o.label ?? o}</button>
      ))}
    </div>
  );
}

export default function OnSiteApply() {
  const { token: tokenParam } = useParams();
  const token = tokenParam || PUBLIC_TOKEN;
  const [data, setData] = useState(null);
  const [nf, setNf] = useState(false);
  const [f, setF] = useState({ firstName: "", lastName: "", email: "", phone: "" });
  const [shift, setShift] = useState(null);
  const [travelPass, setTravelPass] = useState(null);
  const [referralSource, setReferralSource] = useState("");
  const [referralOrganization, setReferralOrganization] = useState("");
  const [positionInterest, setPositionInterest] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => { getOnsite(token).then(setData).catch(() => setNf(true)); }, [token]);

  if (nf) return <div className="mh"><div className="mh-empty">This interview link is not valid.</div></div>;
  if (!data) return <div className="mh"><div className="mh-empty">Loading…</div></div>;

  if (!data.open && !done) return (
    <div className="mh" data-testid="onsite-closed"><div className="mh-wrap ca-wrap">
      <div className="mh-success-ic ca-center"><DoorClosed size={26} /></div>
      <h1 className="ca-confirm-title">Same-day interview registration is closed</h1>
      <p className="ca-lead">On-site interviews at {data.employerName} are not accepting same-day sign-ups right now. You can still register for the next available interview — it only takes a minute.</p>
      <Link className="mh-save" style={{ textAlign: "center" }} to="/apply" data-testid="onsite-to-public">Register for an upcoming interview</Link>
    </div></div>
  );

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const infoValid = f.firstName.trim() && f.lastName.trim() && f.email.trim() && f.phone.trim();
  const needsOrg = NEEDS_ORG.has(referralSource);
  const canSubmit = infoValid && shift && travelPass !== null && referralSource && (!needsOrg || referralOrganization.trim()) && ack;

  const submit = async () => {
    if (!canSubmit || busy) return;
    setErr(""); setBusy(true);
    try {
      const r = await confirmOnsite(token, {
        ...f, sessionDate: null, preferredShift: shift, travelPassRequired: travelPass === true,
        referralSource, referralOrganization: needsOrg ? referralOrganization.trim() : null,
        positionInterest: positionInterest || null, acknowledged: ack,
      });
      setDone(r);
    } catch (e) {
      if (e?.response?.status === 409) { setData({ ...data, open: false }); }
      else { setErr(e?.response?.data?.detail || "Something went wrong. Please try again."); }
      setBusy(false);
    }
  };

  if (done) return (
    <div className="mh"><div className="mh-wrap ca-wrap">
      <div className="mh-success-ic ca-center"><Check size={28} /></div>
      <h1 className="ca-confirm-title">You're registered with Make It Happen Philly</h1>
      <p className="ca-lead" data-testid="onsite-not-offer">You're checked in for today's on-site interview. This is not a job offer and does not guarantee employment.</p>
      <div className="ca-confirm-card" data-testid="onsite-confirmed">
        <div className="ca-emp">{done.name}</div>
        <div className="ca-date">{done.dateLabel}</div>
        <div className="ca-time">{done.time}</div>
        <div className="ca-loc"><MapPin size={15} /> {done.location}</div>
      </div>
      <div className="ca-bring"><h2>What to bring</h2><p>{done.instructions}</p></div>
      {done.travelPassRequired && (
        <div className="ca-travel" data-testid="onsite-travel-pass"><h2>Travel Pass Confirmation</h2>
          <p>This confirmation verifies that you are registered to attend a {done.employerName} employment interview through Make It Happen Philly on <strong>{done.dateLabel}</strong> at <strong>{done.time}</strong> at <strong>{done.location}</strong>. If your residential program, caseworker, or other authorized representative requires verification, please forward this confirmation to them.</p>
        </div>
      )}
      <p className="ca-email-note" data-testid="onsite-email-note">A confirmation email has been sent to <strong>{done.email}</strong>.</p>
      <ContactFooter />
    </div></div>
  );

  return (
    <div className="mh" data-testid="onsite-apply"><div className="mh-wrap ca-wrap">
      <header className="mh-head">
        <div className="mh-logo"><img src="/mihp-logo.png" alt="Make It Happen Philly" /></div>
        <div className="mh-agency">MAKE IT HAPPEN PHILLY</div>
        <h1 className="mh-headtitle">On-Site Interview Registration</h1>
        <div className="mh-headsub">{data.employerName} · you're being interviewed today at {data.interviewTime}. Registering is not a job offer.</div>
      </header>

      <label className="mh-lbl">Your information</label>
      <div className="ca-fields">
        <input className="ca-input" placeholder="First name" value={f.firstName} onChange={set("firstName")} data-testid="onsite-first" />
        <input className="ca-input" placeholder="Last name" value={f.lastName} onChange={set("lastName")} data-testid="onsite-last" />
        <input className="ca-input" type="email" inputMode="email" placeholder="Email (required)" value={f.email} onChange={set("email")} data-testid="onsite-email" />
        <input className="ca-input" type="tel" inputMode="tel" placeholder="Mobile phone" value={f.phone} onChange={set("phone")} data-testid="onsite-phone" />
      </div>

      <label className="mh-lbl">Which shifts are you available to work?</label>
      <Pick options={SHIFTS} value={shift} onChange={setShift} testidPrefix="onsite-shift" />
      <p className="mh-help" data-testid="onsite-shift-note">Tell us your availability — final shift assignment happens only after the employer's Hire decision.</p>

      <label className="mh-lbl">Do you require a travel pass from a caseworker or program to attend?</label>
      <Pick options={[{ v: true, label: "Yes" }, { v: false, label: "No" }]} value={travelPass} onChange={setTravelPass} testidPrefix="onsite-travel" />

      <label className="mh-lbl">How did you hear about this?</label>
      <select className="ca-input" value={referralSource} onChange={(e) => setReferralSource(e.target.value)} data-testid="onsite-referral-source">
        <option value="">Select…</option>
        {REFERRAL_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      {needsOrg && <input className="ca-input" style={{ marginTop: 10 }} placeholder="Referral organization name" value={referralOrganization} onChange={(e) => setReferralOrganization(e.target.value)} data-testid="onsite-referral-org" />}

      {data.positions && data.positions.length > 0 && (
        <>
          <label className="mh-lbl">Position interest (optional)</label>
          <select className="ca-input" value={positionInterest} onChange={(e) => setPositionInterest(e.target.value)} data-testid="onsite-position">
            <option value="">No preference</option>
            {data.positions.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </>
      )}

      <label className="ca-ack" data-testid="onsite-ack">
        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="onsite-ack-check" />
        <span>I understand that registering for an interview does not guarantee employment.</span>
      </label>

      {err && <div className="mh-err" data-testid="onsite-error">{err}</div>}
      <button className="mh-save" disabled={!canSubmit || busy} onClick={submit} data-testid="onsite-submit">{busy ? "Submitting…" : "Check in for today's interview"}</button>
    </div></div>
  );
}
